import React, { useState } from 'react';
import { ShieldCheck, ArrowRight, Smartphone, Lock, ArrowLeft, Loader2, AlertCircle, KeyRound, UserCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * PRODUCTION AUTHORIZED REGISTRY
 * Strictly authorized Executive Admins for the Sales Directorate.
 */
const AUTHORIZED_USERS = [
  { name: "Shalton Ogola", phone: "720535757", pin: "0942", role: "ADMIN", agentNo: "ADM-101" },
  { name: "Nelly Kathomi", phone: "710281858", pin: "5581", role: "ADMIN", agentNo: "ADM-102" },
  { name: "Joyce Mwikali", phone: "724736963", pin: "2317", role: "ADMIN", agentNo: "ADM-103" }
];

interface LoginProps {
  onLogin: (phone: string, name: string, role: string) => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [step, setStep] = useState<'phone' | 'pin'>('phone');
  const [phoneInput, setPhoneInput] = useState('');
  const [pinInput, setPinInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [identifiedUser, setIdentifiedUser] = useState<any>(null);

  const normalizePhone = (input: string) => {
    let clean = input.replace(/\D/g, '');
    if (clean.startsWith('254')) clean = clean.substring(3);
    if (clean.startsWith('0')) clean = clean.substring(1);
    return clean;
  };

  const handleRequestAccess = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const normalized = normalizePhone(phoneInput);
    const user = AUTHORIZED_USERS.find(a => a.phone === normalized);

    if (!user) {
      setError("Access Denied");
      return;
    }

    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setIdentifiedUser(user);
      setStep('pin');
    }, 1000);
  };

  const handleVerifyPin = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    setTimeout(() => {
      if (pinInput === identifiedUser.pin) {
        onLogin(identifiedUser.phone, identifiedUser.name, identifiedUser.role);
      } else {
        setLoading(false);
        setError("Invalid Security PIN.");
        setPinInput('');
      }
    }, 800);
  };

  return (
    <div className="min-h-screen bg-[#0f172a] flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-md bg-white rounded-[3rem] shadow-2xl overflow-hidden relative border border-white/10">
        <div className="absolute top-0 right-0 p-12 opacity-[0.03] pointer-events-none">
          <ShieldCheck size={200} className="text-chui-navy" />
        </div>

        <div className="p-10 lg:p-14 text-left">
          <div className="flex justify-center mb-10">
            <div className={`w-20 h-20 rounded-[2rem] flex items-center justify-center text-chui-yellow shadow-2xl transition-all duration-500 ${step === 'pin' ? 'bg-success shadow-success/40' : 'bg-chui-navy shadow-chui-navy/40'}`}>
              {step === 'phone' ? <Lock size={40} strokeWidth={2.5} /> : <UserCheck size={40} strokeWidth={2.5} />}
            </div>
          </div>

          <div className="text-center mb-12">
            <h1 className="text-3xl font-black text-slate-900 tracking-tight uppercase mb-4">
              {step === 'phone' ? 'Admin Portal' : 'Identity Verified'}
            </h1>
            <p className="text-slate-500 font-bold text-sm leading-relaxed uppercase tracking-wide">
              {step === 'phone' 
                ? 'CHUI Directorate Node. Enter your registered phone number to begin.' 
                : `Welcome, ${identifiedUser.name}. Enter your 4-digit PIN to authorize this Directorate session.`}
            </p>
          </div>

          {error && (
            <div className="mb-8 p-5 bg-red-50 border border-red-100 rounded-2xl flex items-start gap-4 animate-in slide-in-from-top-2">
              <AlertCircle className="text-red-500 shrink-0 mt-0.5" size={18} />
              <p className="text-xs font-black text-red-700 uppercase leading-relaxed text-left">{error}</p>
            </div>
          )}

          <form onSubmit={step === 'phone' ? handleRequestAccess : handleVerifyPin} className="space-y-6">
            {step === 'phone' ? (
              <div className="space-y-6">
                <div className="relative group">
                  <Smartphone className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-chui-navy transition-colors" size={20} />
                  <input
                    type="tel"
                    placeholder="7XX XXX XXX"
                    value={phoneInput}
                    onChange={(e) => setPhoneInput(e.target.value)}
                    required
                    className="w-full pl-16 pr-6 py-6 bg-slate-50 border-2 border-slate-100 rounded-3xl font-black text-slate-900 outline-none focus:ring-8 focus:ring-chui-navy/5 focus:bg-white focus:border-chui-navy transition-all text-xl tracking-widest"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading || phoneInput.length < 9}
                  className="w-full bg-chui-navy text-white py-6 rounded-3xl font-black text-sm uppercase tracking-[0.2em] shadow-2xl shadow-chui-navy/30 hover:bg-slate-800 transition-all flex items-center justify-center disabled:opacity-50 active:scale-[0.98]"
                >
                  {loading ? <Loader2 className="animate-spin" size={20} /> : (
                    <>Verify Access <ArrowRight className="ml-3" size={18} /></>
                  )}
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="relative group">
                  <KeyRound className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-chui-navy transition-colors" size={20} />
                  <input
                    type="password"
                    placeholder="••••"
                    value={pinInput}
                    onChange={(e) => setPinInput(e.target.value.slice(0, 4))}
                    required
                    maxLength={4}
                    className="w-full pl-16 pr-6 py-6 bg-slate-50 border-2 border-slate-100 rounded-3xl font-black text-slate-900 outline-none focus:ring-8 focus:ring-chui-navy/5 focus:bg-white focus:border-chui-navy transition-all text-4xl tracking-[0.8em] text-center"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading || pinInput.length < 4}
                  className="w-full bg-chui-navy text-white py-6 rounded-3xl font-black text-sm uppercase tracking-[0.2em] shadow-2xl shadow-chui-navy/30 hover:bg-slate-800 transition-all flex items-center justify-center disabled:opacity-50 active:scale-[0.98]"
                >
                  {loading ? <Loader2 className="animate-spin" size={20} /> : (
                    <>Authorize Node <ArrowRight className="ml-3" size={18} /></>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => { setStep('phone'); setPinInput(''); }}
                  className="text-slate-400 font-black text-[10px] uppercase tracking-widest hover:text-chui-navy transition-colors py-2 flex items-center justify-center w-full"
                >
                  <ArrowLeft size={12} className="mr-2" /> Switch Identity
                </button>
              </div>
            )}
          </form>
        </div>

        <div className="bg-slate-50 p-8 border-t border-slate-100 text-center flex items-center justify-center gap-4">
          <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em]">
            Executive Uplink Active
          </p>
        </div>
      </div>
      
      <p className="mt-12 text-white/30 font-black text-[10px] uppercase tracking-[0.5em]">
        Production Environment • v1.0.0
      </p>
    </div>
  );
};

export default Login;